/**
 * The Relationships section's pure half: which number a matrix cell shows,
 * what counts as a break, which side of the line a Safe-haven correlations gauge
 * warns on, and the scatter's last-n points with the derive's r and β.
 *
 * Port of `docs/mockups/macro-v2.html` (`glMat`, `glSc`, `GLHH`, `glWarn`).
 * The numbers come from the nightly derive (`api/macro-relationships.ts`); the
 * client only chooses among them and draws — a null is shown as `NO_HISTORY`,
 * never computed locally. Nothing here fetches.
 */

import type { RelationshipsConfig } from "@qufolio/model";
import type { AssetKind, MacroAsset, PairPayload, RelationshipsPayload } from "../api/macro-relationships";

export type RelWindow = NonNullable<RelationshipsConfig["window"]>;
export type RelMode = NonNullable<RelationshipsConfig["mode"]>;

export const REL_WINDOWS: readonly RelWindow[] = ["13w", "52w", "3y"];
export const WINDOW_WEEKS: Readonly<Record<RelWindow, number>> = { "13w": 13, "52w": 52, "3y": 156 };

/** The warning lines offered (plan: "0 (±0.2 / ±0.3 offered)"). View state, not stored. */
export const HEDGE_LINES: readonly number[] = [0, 0.2, 0.3];

/** The mock's matrix order and names (`GLM`, `GLNM`), used until the server's list arrives. */
export const DEFAULT_ORDER: readonly string[] = [
  "SPX", "NDX", "RUT", "EEM", "US10Y", "TLT", "HYG", "GOLD", "SILVER", "WTI", "COPPER", "BTC", "UUP", "VIX",
];

/** `{}` means every default: 52w, correlation. */
export function relConfig(c: RelationshipsConfig | undefined): { window: RelWindow; mode: RelMode } {
  return { window: c?.window ?? "52w", mode: c?.mode ?? "correlation" };
}

/** Correlation per unordered pair, for one window. */
export type PairTable = Map<string, number | null>;

const pk = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);

export function pairTables(p: RelationshipsPayload): Map<number, PairTable> {
  const out = new Map<number, PairTable>();
  for (const [w, m] of Object.entries(p.matrix)) {
    const t: PairTable = new Map();
    m.a.forEach((a, i) => t.set(pk(a, m.b[i]!), m.r[i] ?? null));
    out.set(Number(w), t);
  }
  return out;
}

export function corrOf(tables: Map<number, PairTable>, a: string, b: string, weeks: number): number | null {
  return tables.get(weeks)?.get(pk(a, b)) ?? null;
}

/**
 * What a cell shows: r over the chosen window, or in Change mode 13w − 52w
 * (the window control does not apply to Change, as in the mock). null when
 * either side is missing — a window must be complete (`calc.rolling_pairs`).
 */
export function cellValue(
  tables: Map<number, PairTable>, a: string, b: string, mode: RelMode, window: RelWindow,
): number | null {
  if (mode === "change") {
    const r13 = corrOf(tables, a, b, 13);
    const r52 = corrOf(tables, a, b, 52);
    return r13 == null || r52 == null ? null : r13 - r52;
  }
  return corrOf(tables, a, b, WINDOW_WEEKS[window]);
}

/** Outlined in Change mode only, at |Δ| ≥ the threshold (plan default 0.4). */
export function isBreak(v: number | null, mode: RelMode, threshold: number): boolean {
  return mode === "change" && v != null && Math.abs(v) >= threshold;
}

/** The mock's `sg2`: a sign always, a true minus, two places. */
export function signed2(v: number | null): string {
  if (v == null) return "—";
  return (v >= 0 ? "+" : "−") + Math.abs(v).toFixed(2);
}

/** The cell text: one place, no leading zero (`.4`, `−.7`, Change `+.5`). */
export function cellText(v: number | null, mode: RelMode): string {
  if (v == null) return "";
  const body = Math.abs(v).toFixed(1).replace(/^0/, "");
  return (v < 0 ? "−" : mode === "change" ? "+" : "") + body;
}

// -- Safe-haven correlations --------------------------------------------------

export interface HedgeDef {
  a: string;
  b: string;
  name: string;
  question: string;
  /** +1: warns above the line (stock–bond, bond–dollar); −1: warns below it (bond–gold). */
  side: 1 | -1;
  ok: string;
  bad: string;
  okWord: string;
  badWord: string;
}

/** The mock's `GLHH`, word for word. */
export const HEDGES: readonly HedgeDef[] = [
  {
    a: "SPX", b: "TLT", name: "Stocks vs bonds", question: "Is my hedge working?", side: 1,
    ok: "Bonds rise when stocks fall: the hedge works", bad: "Stocks and bonds fall together: no hedge",
    okWord: "hedge works", badWord: "no hedge",
  },
  {
    a: "TLT", b: "GOLD", name: "Bonds vs gold", question: "Is gold about rates, or about trust?", side: -1,
    ok: "Gold trades on real rates, alongside bonds",
    bad: "Gold has left rates behind: deficits, debasement, central-bank buying",
    okWord: "on rates", badWord: "off rates",
  },
  {
    a: "TLT", b: "UUP", name: "Bonds vs dollar", question: "Is the US a safe haven right now?", side: 1,
    ok: "Higher yields pull money in: the dollar rises as bonds fall",
    bad: "Bonds and the dollar fall together: money is leaving the US",
    okWord: "normal", badWord: "sell America",
  },
];

/** The mock's `glWarn`: the line sits at `side × line`, and the warning is beyond it. */
export function hedgeWarns(side: 1 | -1, v: number | null, line: number): boolean | null {
  if (v == null) return null;
  return side > 0 ? v > line : v < -line;
}

/** Index runs `[from, to]` where the series sits in the warning zone — the dark red fill. */
export function warnRuns(series: ReadonlyArray<number | null>, side: 1 | -1, line: number): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  let start = -1;
  series.forEach((v, i) => {
    const w = hedgeWarns(side, v, line) === true;
    if (w && start < 0) start = i;
    if (!w && start >= 0) {
      out.push([start, i - 1]);
      start = -1;
    }
  });
  if (start >= 0) out.push([start, series.length - 1]);
  return out;
}

/** The last non-null value — "today" on a gauge. */
export function lastValue(s: ReadonlyArray<number | null>): { i: number; v: number } | null {
  for (let i = s.length - 1; i >= 0; i--) {
    const v = s[i];
    if (v != null) return { i, v };
  }
  return null;
}

/** Indices where the calendar year changes, with the new year — gridlines and labels. */
export function yearBreaks(weeks: readonly string[]): Array<{ i: number; year: number }> {
  const out: Array<{ i: number; year: number }> = [];
  let prev: number | null = null;
  weeks.forEach((w, i) => {
    const y = Number(w.slice(0, 4));
    if (prev != null && y !== prev) out.push({ i, year: y });
    prev = y;
  });
  return out;
}

// -- Scatter -------------------------------------------------------------------

/**
 * What a cell, the scatter and a gauge say when the derive returned null for a
 * window: it had fewer complete weeks than the window needs. The client never
 * strikes its own r or β to fill the gap — a number from a short window would
 * read as the window's, and disagree with the matrix.
 */
export const NO_HISTORY = "not enough history for this window";

export interface Scatter {
  points: Array<{ week: string; x: number; y: number }>;
  /** The derive's newest r for the window; null when it had too little history. */
  r: number | null;
  /** The derive's newest β of y on x for the window; null likewise. */
  beta: number | null;
  /** r or β is null: say so ({@link NO_HISTORY}) rather than draw a fit. */
  noHistory: boolean;
  meanX: number;
  meanY: number;
}

/**
 * The window's last `weeks` weekly changes, both present. r and β are the
 * derive's for the newest week of that window — the same numbers the matrix
 * shows. A null there (or no series for the window) is reported as
 * `noHistory`, never filled from the points drawn or from an older week.
 */
export function scatterOf(pair: PairPayload, weeks: number): Scatter {
  const pts: Scatter["points"] = [];
  const { week, x, y } = pair.points;
  for (let i = 0; i < week.length; i++) {
    const xv = x[i], yv = y[i];
    if (xv != null && yv != null) pts.push({ week: week[i]!, x: xv, y: yv });
  }
  const points = pts.slice(-weeks);
  const xs = points.map((p) => p.x), ys = points.map((p) => p.y);
  const n = points.length || 1;
  const meanX = xs.reduce((s, v) => s + v, 0) / n;
  const meanY = ys.reduce((s, v) => s + v, 0) / n;
  const s = pair.series[String(weeks)];
  const r = s?.r.length ? (s.r[s.r.length - 1] ?? null) : null;
  const beta = s?.beta.length ? (s.beta[s.beta.length - 1] ?? null) : null;
  return { points, r, beta, noHistory: r == null || beta == null, meanX, meanY };
}

export function unitOf(kind: AssetKind | undefined): string {
  return kind === "r" ? "pp" : kind === "l" ? "pts" : "%";
}

/** "Gold moves +0.31 % for each +1 pp in 10Y yield (weekly)" — the β line, in words. */
export function betaSentence(a: MacroAsset | undefined, b: MacroAsset | undefined, aKey: string, bKey: string,
  beta: number | null): string {
  return `β: ${b?.name ?? bKey} moves ${signed2(beta)} ${unitOf(b?.kind)} for each +1 ${unitOf(a?.kind)} in ${a?.name ?? aKey} (weekly)`;
}

export function windowLabel(w: RelWindow): string {
  return w === "3y" ? "3y" : w;
}
